import { FC } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { BaseDrawer, FormDrawer, useSelectedBulkBookings } from "@ryde/layout";
import {
  useBookingsBulkActions,
  useModalConfirm,
  usePageContext,
  useYupValidationResolver,
} from "@ryde/redux";
import {
  bulkCloneFormDrawerAF,
  ConfirmResult,
  useTimezone,
  type IBulkCloneBookingsForm,
} from "@ryde/shared";

import { buildBulkCloneBookingsPayload } from "./bulk-clone-form.bookings.utils";
import { useBulkCloneBookingsValidation } from "./bulk-clone-form.bookings.validation";
import { BulkCloneBookingsForm } from "./parts";

export const BulkCloneFormBookingsDrawer: FC = () => {
  const { t } = useTranslation();
  const { entity } = usePageContext();
  const { timezone } = useTimezone();
  const { confirmDialogResult } = useModalConfirm();
  const selectedBookings = useSelectedBulkBookings();
  const { bulkClone } = useBookingsBulkActions();
  const setBulkCloneFormDrawerOpened = useSetRecoilState(
    bulkCloneFormDrawerAF(entity),
  );

  const { validation } = useBulkCloneBookingsValidation();
  const resolver = useYupValidationResolver(validation, entity);
  const methods = useForm<IBulkCloneBookingsForm>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    resolver,
  });
  const { getValues, trigger } = methods;

  const onCloseClick = useEvent(() => {
    setBulkCloneFormDrawerOpened(false);
  });

  const onSaveClick = useEvent(async () => {
    // trigger() resolves with this run's result; formState.isValid can still hold the previous render's.
    const isFormValid = await trigger();
    if (!isFormValid) {
      return;
    }

    const ids = selectedBookings.map(booking => booking.id);
    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t("booking.bulk.copyBookingsConfirmMessage", {
        bookingsCount: ids.length,
      }),
    });
    if (confirmationResult !== ConfirmResult.OK) {
      return;
    }

    const { dateFrom, dateTo, ...form } = getValues();
    // Both are required, and trigger() has just passed; this only narrows the types.
    if (!dateFrom || !dateTo) {
      return;
    }

    const result = await bulkClone(
      buildBulkCloneBookingsPayload({
        ids,
        form: { ...form, dateFrom, dateTo },
        timezone,
      }),
    );
    if (result.success) {
      setBulkCloneFormDrawerOpened(false);
    }
  });

  return (
    <BaseDrawer onCloseDrawer={onCloseClick}>
      <FormDrawer
        onCloseDrawer={onCloseClick}
        onCancelClick={onCloseClick}
        title={t("booking.bulk.copyBookings")}
        onSaveClick={onSaveClick}
        footerSaveLabel={t("booking.bulk.copy")}
      >
        <FormProvider {...methods}>
          <BulkCloneBookingsForm />
        </FormProvider>
      </FormDrawer>
    </BaseDrawer>
  );
};
